import {startOfMonth, endOfMonth, isBefore, isAfter} from 'date-fns';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {detectEventCollisions, type CollisionLayout} from './detectEventCollisions';
import {filterEventsForMonth} from './filterEventsForMonth';
import {
  groupEventsByGroupId,
  type GroupedRentalResourceCalendarEvent,
} from './groupEventsByGroupId';

/**
 * Enhanced grouped event with collision detection information
 */
export interface GroupedEventWithCollisions extends GroupedRentalResourceCalendarEvent {
  collisionLayout: CollisionLayout;
  expandedRowHeight: number;
}

/**
 * Creates grouped events with collision detection for a given month
 *
 * @param currentDate - The current date to filter events for
 * @param events - Array of calendar events to process
 * @returns Array of grouped events with collision detection and layout information
 *
 * @example
 * ```ts
 * const groupedEvents = createGroupedEventsWithCollisions(new Date(), events);
 * // Returns events grouped by resource with collision layout calculated
 * ```
 */
export function createGroupedEventsWithCollisions(
  currentDate: Date,
  events: RentalResourceCalendarEvent[]
): GroupedEventWithCollisions[] {
  const monthFilteredEvents = filterEventsForMonth(events, currentDate);
  const groupedEvents = groupEventsByGroupId(monthFilteredEvents);

  const groupedEventsWithCollisions = groupedEvents.map(
    (groupedEvent): GroupedEventWithCollisions => {
      const collisionLayout = detectEventCollisions(groupedEvent.originalEvents);
      const expandedRowHeight = CALENDAR_CONSTANTS.ROW_HEIGHT * collisionLayout.maxRowsNeeded;

      return {
        ...groupedEvent,
        collisionLayout,
        expandedRowHeight,
      };
    }
  );

  // Sort events: cross-month events first (alphabetically), then regular events (alphabetically)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  return groupedEventsWithCollisions.sort((a, b) => {
    const aCrossMonth = isBefore(a.start, monthStart) || isAfter(a.end, monthEnd);
    const bCrossMonth = isBefore(b.start, monthStart) || isAfter(b.end, monthEnd);

    // If one is cross-month and the other isn't, cross-month comes first
    if (aCrossMonth && !bCrossMonth) {
      return -1;
    }
    if (!aCrossMonth && bCrossMonth) {
      return 1;
    }

    // Both are same type (cross-month or regular), sort alphabetically by vehicle title
    const aTitle = a.rentalOrder?.vehicleTitle || a.title || '';
    const bTitle = b.rentalOrder?.vehicleTitle || b.title || '';
    return aTitle.localeCompare(bTitle);
  });
}
